import React, { useState, useMemo, useEffect } from 'react';
import { PlusCircle, Eye, Edit2, Trash2, Download } from 'lucide-react';

import { DateRangeFilter } from '../../components/ui/DateRangeFilter';
import { OrderFormModal } from './OrderFormModal';
import { OrderDetailModal } from './OrderDetailModal';
import { ConfirmationModal } from '../../components/ui/ConfirmationModal';
import { CustomModal } from '../../components/ui/CustomModal';
import { Pagination } from '../../components/ui/Pagination';
import { ActionMenu, ActionMenuItem } from '../../components/ui/ActionMenu';
import { formatCurrency, formatDateForDisplay, calculateOrderProfit, exportToExcel } from '../../utils/helpers';

const ITEMS_PER_PAGE = 20;

export const Orders = ({ orders, allOrders, onSaveOrder, onDeleteOrder, onUpdateOrderField }) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingOrder, setEditingOrder] = useState(null);
    const [deletingOrder, setDeletingOrder] = useState(null);
    const [viewOrderModal, setViewOrderModal] = useState({ isOpen: false, order: null });
    const [viewingCustomer, setViewingCustomer] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [statusFilter, setStatusFilter] = useState('All');
    const [sourceFilter, setSourceFilter] = useState('All');

    const statusOptions = ["Mới", "Đang tìm hàng", "Đã đặt hàng", "Đã giao hàng", "Đã hoàn thành", "Đã đối soát", "Đã hoàn", "Hủy"];
    const sourceOptions = ['All', 'Trực tiếp', 'Website', 'Oreka', 'Lazada'];

    const filteredOrders = useMemo(() => {
        return [...orders] 
            .filter(order => {
                const isDateInRange = (() => {
                    if (!startDate || !endDate) return true;
                    const orderDate = new Date(order.date);
                    const start = new Date(startDate);
                    start.setHours(0,0,0,0);
                    const end = new Date(endDate);
                    end.setHours(23,59,59,999);
                    return orderDate >= start && orderDate <= end;
                })();
                const isStatusMatch = statusFilter === 'All' || order.status === statusFilter;
                const isSourceMatch = sourceFilter === 'All' || order.source === sourceFilter;
                return isDateInRange && isStatusMatch && isSourceMatch;
            })
    }, [orders, startDate, endDate, statusFilter, sourceFilter]);

    const paginatedOrders = useMemo(() => {
        const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
        return filteredOrders.slice(startIndex, startIndex + ITEMS_PER_PAGE);
    }, [filteredOrders, currentPage]);

    const customerOrders = useMemo(() => {
        if (!viewingCustomer) return [];
        return allOrders
            .filter(order => order.phone === viewingCustomer.phone)
            .sort((a,b) => new Date(b.date) - new Date(a.date));
    }, [viewingCustomer, allOrders]);

    useEffect(() => {
        setCurrentPage(1);
    }, [startDate, endDate, statusFilter, sourceFilter]);


    const getStatusClass = (status) => ({
        'Đã hoàn thành': 'bg-green-100 text-green-800',
        'Đã hoàn': 'bg-cyan-100 text-cyan-800',
        'Đã giao hàng': 'bg-blue-100 text-blue-800',
        'Đang tìm hàng': 'bg-yellow-100 text-yellow-800',
        'Đã đặt hàng': 'bg-orange-100 text-orange-800',
        'Mới': 'bg-purple-100 text-purple-800',
        'Đã đối soát': 'bg-teal-100 text-teal-800',
        'Hủy': 'bg-red-100 text-red-800',
    }[status] || 'bg-gray-100 text-gray-800');

    const getSourceColor = (source) => {
        switch (source) {
            case 'Lazada': return 'text-purple-600';
            case 'Oreka': return 'text-orange-600';
            case 'Website': return 'text-red-600';
            case 'Trực tiếp': return 'text-yellow-600';
            default: return 'text-gray-600';
        }
    };

    const handleEdit = (order) => {
        setEditingOrder(order);
        setIsModalOpen(true);
    };

    const handleAddNew = () => {
        setEditingOrder(null);
        setIsModalOpen(true);
    };

    const handleSave = (orderData) => {
        onSaveOrder(orderData, !!editingOrder);
        setIsModalOpen(false);
        setEditingOrder(null);
    };
    
    const handleDeleteConfirm = () => {
        onDeleteOrder(deletingOrder.id);
        setDeletingOrder(null);
    }

    const calculateTotalAmount = (order) => {
        if (!Array.isArray(order.items)) return 0;
        const itemsTotal = order.items.reduce((sum, item) => sum + item.quantity * item.price, 0);
        return itemsTotal + (order.shippingFee || 0) - (order.promotion || 0);
    }
    
    const handleExport = () => {
        const dataToExport = filteredOrders.map(order => {
            const { profit } = calculateOrderProfit(order);
            return {
                'Mã Đơn': order.id,
                'Tên Khách Hàng': order.customerName,
                'Số Điện Thoại': order.phone,
                'Ngày Đặt': formatDateForDisplay(order.date),
                'Sản Phẩm': order.items.map(item => `${item.productName} (x${item.quantity})`).join(', '),
                'Nguồn': order.source,
                'Nhãn': order.label,
                'Kho Đóng Gói': order.packingWarehouse,
                'Tổng Tiền Hàng': order.items.reduce((sum, item) => sum + item.quantity * item.price, 0),
                'Giá Vốn': order.items.reduce((sum, item) => sum + item.quantity * item.cost, 0),
                'Phí Ship Báo Khách': order.shippingFee,
                'Phí Ship Thực Trả': order.realShippingFee,
                'Khuyến Mãi': order.promotion,
                'Phí Sàn': order.platformFee,
                'Tổng Thu': calculateTotalAmount(order),
                'Lợi Nhuận': profit,
                'Trạng Thái': order.status
            };
        });
        exportToExcel(dataToExport, 'DonHang');
    };

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h2 className="text-3xl font-bold text-gray-800">Tất cả đơn hàng</h2>
                <div className="flex gap-2">
                    <button onClick={handleExport} className="flex items-center gap-2 bg-green-600 text-white font-semibold py-2 px-4 rounded-lg shadow-md hover:bg-green-700 transition">
                        <Download size={20} /> Xuất Excel
                    </button>
                    <button onClick={handleAddNew} className="flex items-center gap-2 bg-blue-600 text-white font-semibold py-2 px-4 rounded-lg shadow-md hover:bg-blue-700 transition">
                        <PlusCircle size={20} /> Thêm đơn hàng
                    </button>
                </div>
            </div>

            <div className="flex items-center gap-4">
                <DateRangeFilter startDate={startDate} setStartDate={setStartDate} endDate={endDate} setEndDate={setEndDate} />
                <div className="flex items-center gap-2 bg-white p-4 rounded-lg shadow-sm">
                    <label htmlFor="source-filter-all" className="text-sm font-medium text-gray-700">Nguồn đơn:</label>
                    <select id="source-filter-all" value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)} className="p-2 border rounded-md bg-white">
                        {sourceOptions.map(source => <option key={source} value={source}>{source}</option>)}
                    </select>
                </div>
                <div className="flex items-center gap-2 bg-white p-4 rounded-lg shadow-sm">
                    <label htmlFor="status-filter" className="text-sm font-medium text-gray-700">Trạng thái:</label>
                    <select id="status-filter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="p-2 border rounded-md bg-white">
                        <option value="All">Tất cả</option>
                        {statusOptions.map(status => <option key={status} value={status}>{status}</option>)}
                    </select>
                </div>
            </div>

            <OrderFormModal isOpen={isModalOpen} onClose={() => {setIsModalOpen(false); setEditingOrder(null)}} onSave={handleSave} existingOrder={editingOrder} />
            <OrderDetailModal isOpen={viewOrderModal.isOpen} onClose={() => setViewOrderModal({ isOpen: false, order: null })} order={viewOrderModal.order} />
            <ConfirmationModal isOpen={!!deletingOrder} onClose={() => setDeletingOrder(null)} onConfirm={handleDeleteConfirm} title="Xác nhận Xóa" message={`Bạn có chắc muốn xóa đơn hàng ${deletingOrder?.id}? Hành động này không thể hoàn tác.`} />
            
            <CustomModal isOpen={!!viewingCustomer} onClose={() => setViewingCustomer(null)} title={`Lịch sử mua hàng: ${viewingCustomer?.name}`} maxWidth="max-w-6xl">
                 <div className="overflow-x-auto">
                    <table className="w-full text-left">
                        <thead className="border-b bg-gray-50">
                            <tr>
                                {['Mã Đơn', 'Ngày Đặt', 'Sản phẩm đã mua', 'Tổng Tiền', 'Trạng Thái'].map(h => <th key={h} className="p-3 font-semibold text-gray-600">{h}</th>)}
                            </tr>
                        </thead>
                         <tbody>
                            {customerOrders.map(order => (
                                <tr key={order.id} className="border-b">
                                    <td className="p-3 font-medium text-blue-600 align-top">{order.id}</td>
                                    <td className="p-3 align-top">{formatDateForDisplay(order.date)}</td>
                                    <td className="p-3 align-top">
                                        <ul className="list-disc list-inside text-sm">
                                            {order.items.map((item, index) => (
                                                <li key={index}>{item.productName} (x{item.quantity})</li>
                                            ))}
                                        </ul>
                                    </td>
                                    <td className="p-3 align-top">{formatCurrency(order.items.reduce((s,i) => s + i.price * i.quantity, 0))}</td>
                                    <td className="p-3 align-top">{order.status}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                 </div>
            </CustomModal>


            <div className="bg-white rounded-lg shadow-md">
                <div className="overflow-x-auto p-6">
                       <table className="w-full text-left">
                            <thead className="border-b bg-gray-50">
                                <tr>
                                    {['Khách Hàng', 'Ngày Đặt', 'Nguồn đơn', 'Sản phẩm', 'Tổng Tiền', 'Lợi nhuận', 'Trạng Thái', 'Hành động'].map(h => <th key={h} className="p-4 font-semibold text-gray-600">{h}</th>)}
                                </tr>
                            </thead>
                            <tbody>
                                {paginatedOrders.map(order => {
                                    const { profit, percentage } = calculateOrderProfit(order);
                                    const profitColor = profit === null ? 'text-gray-400' : profit >= 0 ? 'text-green-600' : 'text-red-600';
                                   
                                    return (
                                    <tr key={order.id} className="border-b hover:bg-gray-50">
                                        <td className="p-4">
                                            <button onClick={() => setViewingCustomer({ name: order.customerName, phone: order.phone })} className="text-gray-800 font-medium hover:text-blue-600 hover:underline">
                                                {order.customerName}
                                            </button>
                                        </td>
                                        <td className="p-4 text-gray-800">{formatDateForDisplay(order.date)}</td>
                                        <td className="p-4">
                                            <div className={`font-bold ${getSourceColor(order.source)}`}>{order.source}</div>
                                            <div className="text-xs font-mono text-gray-500">{order.id}</div>
                                        </td>
                                        <td className="p-4 text-gray-800">
                                            <ul className="list-disc list-inside text-sm">
                                                {order.items.map(item => <li key={item.productName}>{item.productName}</li>)}
                                            </ul>
                                        </td>
                                        <td className="p-4 text-gray-800 font-medium">{formatCurrency(calculateTotalAmount(order))}</td>
                                        <td className="p-4">
                                            {profit === null ? (
                                                <span className="text-gray-400">-</span>
                                            ) : (
                                                <div className={`font-semibold ${profitColor}`}>
                                                    {formatCurrency(profit)}
                                                    <span className="text-xs ml-1">({percentage.toFixed(1)}%)</span>
                                                </div>
                                            )}
                                        </td>
                                        <td className="p-4">
                                             <select 
                                                value={order.status} 
                                                onChange={(e) => onUpdateOrderField(order.id, 'status', e.target.value)}
                                                className={`px-3 py-1.5 text-xs font-bold rounded-full border-0 outline-none appearance-none ${getStatusClass(order.status)}`}
                                            >
                                                {statusOptions.map(status => <option key={status} value={status}>{status}</option>)}
                                            </select>
                                        </td>
                                        <td className="p-4">
                                            <ActionMenu>
                                                <ActionMenuItem icon={<Eye size={16} />} label="Xem chi tiết" onClick={() => setViewOrderModal({isOpen: true, order: order})} />
                                                <ActionMenuItem icon={<Edit2 size={16} />} label="Sửa đơn hàng" onClick={() => handleEdit(order)} />
                                                <ActionMenuItem icon={<Trash2 size={16} />} label="Xóa đơn hàng" onClick={() => setDeletingOrder(order)} />
                                            </ActionMenu>
                                        </td>
                                    </tr>
                                )})}
                            </tbody>
                        </table>
                </div>
                <Pagination
                    currentPage={currentPage}
                    totalItems={filteredOrders.length}
                    itemsPerPage={ITEMS_PER_PAGE}
                    onPageChange={setCurrentPage}
                />
            </div>
        </div>
    );
};
